import { Component, Inject, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { trackByRef } from 'src/app/lego-utils';
import { isEmpty } from 'lodash-es';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectItemInterface } from 'ng-zorro-antd/select';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { EntryCode, MouldLogEntry } from 'src/app/models';
import { NotificationDataService } from 'src/app/services';
import { IPage, MouldLogService, PagedCollection } from 'src/app/state';
import { EquipmentLookupService } from 'src/app/state/equipment/equipment-lookup.service';
import { MOULD_LOG_PAGINATOR } from './mould-log-pagination.provider';

@Component({
  selector: 'em-mould-log',
  styleUrls: ['./mould-log.component.less'],
  templateUrl: './mould-log.component.html',
  standalone: false,
})
export class MouldLogComponent {
  @ViewChild('longTextTemplate', { static: true })
  private readonly longTextTemplate!: TemplateRef<{ $implicit: string }>;

  public readonly trackByRef = trackByRef;
  public readonly error$ = this.mouldLogService.error$;
  public readonly isEquipmentLoading$ = this.equipmentLookup.loading$;

  public perPage = 10;
  public pageIndex = 1;

  public readonly perPage$ = new BehaviorSubject<number>(this.perPage);
  public readonly selectedEntryTypes$ = new BehaviorSubject<string[]>([]);
  public readonly pagination$: Observable<IPage<MouldLogEntry>>;
  public readonly entryCodes$: Observable<EntryCode[]>;
  public readonly entryCodesControl = new FormControl<EntryCode[]>([], { nonNullable: true });
  public areEntryCodesLoading = false;

  public constructor(
    @Inject(MOULD_LOG_PAGINATOR) public readonly paginatorRef: PagedCollection<MouldLogEntry>,
    private readonly notificationDataService: NotificationDataService,
    private readonly mouldLogService: MouldLogService,
    private readonly modalService: NzModalService,
    private readonly equipmentLookup: EquipmentLookupService
  ) {
    const equipmentNumber$ = this.equipmentLookup.selectedEquipmentId$.pipe(
      tap(() => {
        // Set loading to avoid showing "no data" until "this.paginatorRef.getPage(...)" is called
        this.paginatorRef.setLoading(true);

        // clear error on new equipment else the html will keep showing the error,
        // and never try to fetch the mould log for the new equipment
        this.mouldLogService.clearError();

        // entry codes technically start loading here
        this.areEntryCodesLoading = true;

        this.paginatorRef.setPage(1);
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    this.entryCodes$ = equipmentNumber$.pipe(
      switchMap((equipmentNumber) => {
        if (equipmentNumber === undefined) {
          return of([]);
        }

        return this.notificationDataService
          .getEntryCodesByEquipmentNumber(
            equipmentNumber,
            ['ZE'] // ZE = mould log
          )
          .pipe(catchError(() => of([])));
      }),
      tap((newEntryCodes) => {
        this.areEntryCodesLoading = false;

        const selectedEntryCodes: EntryCode[] = this.entryCodesControl.value;

        const filteredEntryCodes = selectedEntryCodes.filter((entryCode) =>
          newEntryCodes.some((newEntryCode) => entryCode.code === newEntryCode.code)
        );

        // Remove codes no longer present in the list
        this.entryCodesControl.setValue(filteredEntryCodes);
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    const selectedEntryCodes$ = this.entryCodesControl.valueChanges.pipe(
      map((entryCodes: EntryCode[]) => {
        if (isEmpty(entryCodes)) {
          return undefined;
        }

        return entryCodes.map((entryCode) => entryCode.code);
      }),
      startWith(undefined)
    );

    this.pagination$ = combineLatest([
      this.paginatorRef.pageChanges.pipe(
        tap((page) => {
          this.pageIndex = page;
        })
      ),
      equipmentNumber$,
      selectedEntryCodes$,
      this.perPage$,
    ]).pipe(
      tap(() => {
        this.paginatorRef.clearCache();
      }),
      debounceTime(0), // wait for inputs to stabilize
      switchMap(([page, equipmentNumber, selectedEntryCodes, perPage]) => {
        if (equipmentNumber === undefined) {
          return this.paginatorRef.getPage(() =>
            of({
              currentPage: 1,
              data: [],
              lastPage: 1,
              perPage,
            })
          );
        }

        return this.paginatorRef.getPage(() =>
          this.mouldLogService.loadMouldLog({ page, perPage }, equipmentNumber).pipe(
            map((response) => {
              if (!selectedEntryCodes || selectedEntryCodes.length === 0) {
                return response;
              }

              // Apply client-side filtering
              const filteredData = response.data.filter(
                (entry) =>
                  typeof entry.codeNumber === 'string' &&
                  selectedEntryCodes.includes(entry.codeNumber)
              );

              const total = filteredData.length;
              const start = (page - 1) * perPage;
              const end = start + perPage;
              const paginated = filteredData.slice(start, end);

              return {
                currentPage: page,
                data: paginated,
                lastPage: Math.ceil(total / perPage),
                perPage,
                total,
              };
            })
          )
        );
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  public showLongText(longText: string) {
    const modal = this.modalService.create({
      nzClosable: true,
      nzContent: this.longTextTemplate,
      nzData: longText,
      nzFooter: [
        {
          label: 'Close',
          onClick: () => {
            modal.destroy();
          },
        },
      ],
      nzMaskClosable: true,
      nzTitle: 'Long text',
      nzWidth: '80%',
    });
  }

  public pageSizeChange(perPage: number) {
    this.perPage$.next(perPage);
  }

  public entryCodeFilter(input: string, option: NzSelectItemInterface): boolean {
    const value: EntryCode = option.nzValue as EntryCode;

    return `${value.code} - ${value.text}`.toLowerCase().includes(input.toLowerCase());
  }
}
